import { useEffect, useRef, useState, useCallback } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useDispatch, useSelector } from 'react-redux';
import { exportFormToPDF } from 'actions/formActions';
import { FormConfig_ICS207 } from './FormConfig_ICS207';
import generateUUID from 'utils/sharedUtils/generateUUID';
import { isBrowser } from 'react-device-detect';
import { Config207 } from './Config207';
import dayjs from 'dayjs';
import { Modal } from 'react-bootstrap';
import IncidentRosterBlockChart from './incidentRosterChart/IncidentRosterBlockChart';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import {
  useCurrentOperationalPeriod,
  useFutureOperationalPeriod,
  useIncident,
} from 'components/IAP/hooks/incidentHooks';

export default function OrganizationChart({
  incidentMembers,
  simplified = false,
  isDraft = false,
}) {
  const reduxDispatch = useDispatch();

  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [showPDFPreviewNewTab, setShowPDFPreviewNewTab] = useState(false);
  const [showPDFPreviewMobile, setShowPDFPreviewMobile] = useState(false);
  const [showPDFPreviewTeams, setShowPDFPreviewTeams] = useState(false);
  const [formData207, setFormData207] = useState(null);
  const [formRecordData, setFormRecordData] = useState();
  const [form, setForm] = useState(null);
  const [formConfig, setFormConfig] = useState({});
  const [formState, setFormState] = useState(Config207);

  const reduxCurrentIncident = useSelector((state) => {
    return state.app.currentIncident;
  });
  const reduxCurrentlySelectedGroup = useSelector((state) => {
    return state.app.currentlySelectedGroup;
  });
  const exportedFormsToPDF = useSelector((state) => {
    return state.app.exportedFormsToPDF;
  });
  const user = useSelector((state) => {
    return state.app.user;
  });
  const { data: incidentInfo } = useIncidentInfo();
  const { data: currentPeriod } = useCurrentOperationalPeriod();
  const { data: futurePeriod } = useFutureOperationalPeriod();
  const { data: incidentData } = useIncident();
  const currentPeriodStartDateTime = currentPeriod?.start_date_time;
  const currentPeriodAnticipatedEndDateTime =
    currentPeriod?.anticipated_end_date_time;
  const operationalPeriodInterval = incidentData?.operational_period_interval;

  const formId = useRef(form?.id || generateUUID());
  const collectionId = useRef(form?.collection_id || generateUUID());

  const updateFormState = useCallback(
    (key, value) => {
      const index = formState.field.findIndex((field) => field.key === key);
      if (index !== -1) {
        let obj = { ...formState.field[index] };
        obj = { ...obj, value: value };
        //eslint-disable-next-line
        formState.field[index] = obj;
        setFormState(formState);
        setFormRecordData(formState);
      }
    },
    [formState]
  );

  const initializeFormCollection = useCallback(
    (formname) => {
      const newCollectionId = { ...collectionId }.current || generateUUID();
      return {
        collection_id: newCollectionId,
        formname: formname,
        incident_id: reduxCurrentIncident?.id,
        collection_form_records: [
          {
            collection_id: newCollectionId,
            formname: formname,
            incident_id: reduxCurrentIncident?.id,
            id: generateUUID(),
            record_data: {},
          },
        ],
      };
    },
    [collectionId, reduxCurrentIncident?.id]
  );

  useEffect(() => {
    const incidentCommanders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Incident Commander'
      )
    );

    const publicInformationOfficers = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Public Information Officer'
      )
    );
    const safetyOfficers = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Safety Officer'
      )
    );
    const liaisonOfficers = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Liaison Officer'
      )
    );
    const medicalUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Medical Unit Leader'
      )
    );
    const operationsSectionChiefs = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Operations Section Chief'
      )
    );
    const planningSectionChiefs = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Planning Section Chief'
      )
    );
    const logisticsSectionChiefs = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Logistics Section Chief'
      )
    );
    const financeSectionChiefs = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Finance Section Chief'
      )
    );
    const communicationsUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Communications Unit Leader'
      )
    );
    const stagingAreaManagers = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Staging Area Manager'
      )
    );
    const resourceUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Resource Unit Leader'
      )
    );
    const situationUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Situation Unit Leader'
      )
    );
    const documentationUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Documentation Unit Leader'
      )
    );
    const demobilizationUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Demobilization Unit Leader'
      )
    );
    const supportBranchDirectors = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Support Branch Director'
      )
    );
    const serviceBranchDirectors = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Service Branch Director'
      )
    );
    const supplyUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Supply Unit Leader'
      )
    );
    const facilitiesUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Facilities Unit Leader'
      )
    );
    const groundSupportUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Ground Support Unit Leader'
      )
    );
    const foodUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Food Unit Leader'
      )
    );
    const timeUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Time Unit Leader'
      )
    );
    const procurementUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Procurement Unit Leader'
      )
    );
    const compensationUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Compensation Unit Leader'
      )
    );
    const costUnitLeaders = incidentMembers.filter((member) =>
      member.incidentPositions.some(
        (position) => position.positionName === 'Cost Unit Leader'
      )
    );

    const orgChartValues = [
      {
        key: 'Incident Commanders_2',
        type: 'List',
        value: incidentCommanders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Liaison Officer_3',
        type: 'List',
        value: liaisonOfficers.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },

      {
        key: 'Safety Officer_4',
        type: 'List',
        value: safetyOfficers.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Public Information Officer_2',
        type: 'List',
        value: publicInformationOfficers.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Planning Section Chief_3',
        type: 'List',
        value: planningSectionChiefs.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Operations Section ChiefRow1',
        type: 'List',
        value: operationsSectionChiefs.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Staging Area Manager',
        type: 'List',
        value: stagingAreaManagers.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Resources Unit Ldr',
        type: 'List',
        value: resourceUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Situation Unit Ldr',
        type: 'List',
        value: situationUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Documentation Unit Ldr',
        type: 'List',
        value: documentationUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Demobilization Unit Ldr',
        type: 'List',
        value: demobilizationUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Support Branch Dir',
        type: 'List',
        value: supportBranchDirectors.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Supply Unit Ldr',
        type: 'List',
        value: supplyUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Facilities Unit Ldr',
        type: 'List',
        value: facilitiesUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Ground Spt Unit Ldr',
        type: 'List',
        value: groundSupportUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Food Unit Ldr',
        type: 'List',
        value: foodUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Time Unit Ldr',
        type: 'List',
        value: timeUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Procurement Unit Ldr',
        type: 'List',
        value: procurementUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'CompClaims Unit Ldr',
        type: 'List',
        value: compensationUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Cost Unit Ldr',
        type: 'List',
        value: costUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'FinanceAdmin Section Chief',
        type: 'List',
        value: financeSectionChiefs.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Logistics Section Chief_2',
        type: 'List',
        value: logisticsSectionChiefs.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Medical Unit Ldr',
        type: 'List',
        value: medicalUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Comms Unit Ldr',
        type: 'List',
        value: communicationsUnitLeaders.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
      {
        key: 'Service Branch Dir',
        type: 'List',
        value: serviceBranchDirectors.map(
          (member) =>
            `${member.active === 1 && member.activeTime ? '* ' : ''}${member.profileSettings.userName || member.emailAddress}`
        ),
      },
    ];
    updateFormState('Organization Chart', orgChartValues);

    setFormData207(initializeFormCollection('ICS 207'));
  }, [incidentMembers, updateFormState, initializeFormCollection]);

  useEffect(() => {
    if (formData207 && formData207?.collection_id) {
      setFormConfig(FormConfig_ICS207);

      setForm({
        ...formData207?.collection_form_records[
          formData207?.collection_form_records.length - 1
        ],
        record_state:
          formData207?.collection_form_records[
            formData207?.collection_form_records.length - 1
          ].record_state ||
          (formConfig?.form_states?.length
            ? formConfig?.form_states[0]?.state_name
            : ''),
      });
    }
  }, [formData207, formConfig?.form_states]);

  useEffect(() => {
    if (reduxCurrentIncident) {
      updateFormState('1 Incident Name_11', reduxCurrentIncident?.name);
      const opStartDateTime = isDraft
        ? futurePeriod?.initial_planned_start
        : currentPeriodStartDateTime;
      const opEndDateTime = (() => {
        if (isDraft) {
          return futurePeriod?.initial_planned_end;
        }
        if (currentPeriodAnticipatedEndDateTime) {
          return currentPeriodAnticipatedEndDateTime;
        }
        return null;
      })();

      updateFormState(
        'Date From',
        opStartDateTime ? dayjs(opStartDateTime).format('MM/DD/YYYY') : ''
      );
      updateFormState(
        'Time From',
        opStartDateTime ? dayjs(opStartDateTime).format('HH:mm') : ''
      );
      updateFormState(
        'Date To',
        opEndDateTime ? dayjs(opEndDateTime).format('MM/DD/YYYY') : ''
      );
      updateFormState(
        'Time To',
        opEndDateTime ? dayjs(opEndDateTime).format('HH:mm') : ''
      );

      if (user) {
        updateFormState(
          '4 Prepared by Name_2',
          user.user_name || user.emailAddress
        );
      }

      updateFormState(
        'PositionTitle_9',
        incidentMembers
          ?.find((item) => item.userId === user.user_guid)
          ?.incidentPositions.map((pos) => pos.positionName)
          .join(', ') || ''
      );

      updateFormState('DateTime_11', dayjs().format('MM/DD/YYYY HH:mm:ss'));
    }
  }, [
    reduxCurrentIncident,
    user,
    incidentMembers,
    updateFormState,
    incidentInfo,
    isDraft,
    currentPeriodStartDateTime,
    currentPeriodAnticipatedEndDateTime,
    futurePeriod?.initial_planned_start,
    futurePeriod?.initial_planned_end,
    operationalPeriodInterval,
  ]);

  const b64toBlob = (b64Data) => {
    b64Data = b64Data.replace(/^[\w\d;:\/]+base64\,/g, '');
    const byteArray = Uint8Array.from(
      atob(b64Data)
        .split('')
        .map((char) => char.charCodeAt(0))
    );
    return new Blob([byteArray], { type: 'application/pdf' });
  };

  const createForm = () => {
    const editedForm = { ...form };
    editedForm['id'] = { ...formId }.current;
    editedForm['formname'] = formData207.formname;
    editedForm['created_by_user'] = user.user_guid;
    editedForm['incident_id'] = reduxCurrentIncident.id;
    editedForm['group_guid'] = reduxCurrentlySelectedGroup.group_guid;
    editedForm['record_data'] = formRecordData;
    editedForm['collection_id'] = { ...collectionId }.current;
    return editedForm;
  };

  const handleReloadPDFPreview = (force) => {
    if (showPDFPreview || force) {
      reduxDispatch(exportFormToPDF(createForm(), FormConfig_ICS207));
    }
  };

  useEffect(() => {
    if (
      ((sessionStorage['isUsingTeams'] === 'true' && showPDFPreviewNewTab) ||
        showPDFPreviewMobile) &&
      !!exportedFormsToPDF &&
      exportedFormsToPDF?.length
    ) {
      const pdfContent = URL.createObjectURL(
        b64toBlob(
          exportedFormsToPDF.find((f) => f.id === { ...formId }.current)
            ?.PDFData,
          'application/pdf'
        )
      );
      setShowPDFPreviewNewTab(false);
      window.open(pdfContent, '_blank');
    }
  }, [showPDFPreviewMobile, exportedFormsToPDF, showPDFPreviewNewTab]);

  return (
    <>
      <>
        {!simplified && (
          <div className="d-md-flex align-items-center mb-3">
            <h4>{`${isDraft ? '[DRAFT] - ' : ''}Organization Chart`}</h4>
            <div className="text-md-end ms-auto">
              <StylishNewButton
                className="button--secondary mt-md-0 w-md-auto ms-auto mt-2 w-100"
                onClick={() => {
                  if (!showPDFPreview) {
                    handleReloadPDFPreview(true);
                  }
                  if (isBrowser) {
                    setShowPDFPreview(!showPDFPreview);
                  } else {
                    setShowPDFPreviewMobile(true);
                  }
                  if (sessionStorage['isUsingTeams'] === 'true') {
                    setShowPDFPreviewNewTab(true);
                    setShowPDFPreviewTeams(true);
                  }
                }}
              >
                Print Organization Chart
              </StylishNewButton>
            </div>
          </div>
        )}
        <IncidentRosterBlockChart incidentMembers={incidentMembers} />
      </>
      {isBrowser ? (
        <>
          {showPDFPreview && (
            <Modal
              show={showPDFPreview}
              onHide={() => setShowPDFPreview(false)}
              fullscreen={true}
            >
              <Modal.Header closeButton closeVariant="white">
                <Modal.Title>
                  {formConfig.formname === 'ICS 207' ? (
                    <>Organization Chart: ICS 207</>
                  ) : null}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="pt-2">
                {!!exportedFormsToPDF &&
                  sessionStorage['isUsingTeams'] !== 'true' &&
                  !!exportedFormsToPDF.find(
                    (f) => f.id === { ...formId }.current
                  ) && (
                    <div className="h-100">
                      <iframe
                        style={{ border: 'none' }}
                        src={`data:application/pdf;base64,${
                          exportedFormsToPDF.find(
                            (f) => f.id === { ...formId }.current
                          ).PDFData
                        }`}
                        type="application/pdf"
                        width="100%"
                        height="100%"
                      />
                    </div>
                  )}
              </Modal.Body>
            </Modal>
          )}
        </>
      ) : (
        <Modal
          show={showPDFPreviewMobile}
          onHide={() => setShowPDFPreviewMobile(false)}
          fullscreen={true}
        >
          <Modal.Header closeButton closeVariant="white">
            <Modal.Title>
              {formConfig.formname === 'ICS 207' ? (
                <>Organization Chart: ICS 207</>
              ) : null}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-2">
            {!!exportedFormsToPDF &&
              sessionStorage['isUsingTeams'] !== 'true' &&
              !!exportedFormsToPDF.find(
                (f) => f.id === { ...formId }.current
              ) && (
                <div className="h-100">
                  <iframe
                    style={{ border: 'none' }}
                    src={`data:application/pdf;base64,${
                      exportedFormsToPDF.find(
                        (f) => f.id === { ...formId }.current
                      ).PDFData
                    }`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                  />
                </div>
              )}
          </Modal.Body>
        </Modal>
      )}

      {showPDFPreviewTeams && sessionStorage['isUsingTeams'] === 'true' ? (
        <Modal
          show={showPDFPreviewTeams}
          onHide={() => setShowPDFPreviewTeams(false)}
          fullscreen={true}
        >
          <Modal.Header closeButton closeVariant="white">
            <Modal.Title>
              {formConfig.formname === 'ICS 201' ? (
                <>Incident Briefing: ICS 201</>
              ) : null}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-2">
            {!!exportedFormsToPDF &&
              sessionStorage['isUsingTeams'] === 'true' &&
              !!exportedFormsToPDF.find(
                (f) => f.id === { ...formId }.current
              ) && (
                <div className="h-100">
                  <a
                    href={URL.createObjectURL(
                      b64toBlob(
                        exportedFormsToPDF.find(
                          (f) => f.id === { ...formId }.current
                        )?.PDFData,
                        'image/png'
                      )
                    )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View PDF in Browser
                  </a>
                </div>
              )}
          </Modal.Body>
        </Modal>
      ) : null}
    </>
  );
}
